import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react'

import { Modal } from '@inno-ui/modal'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { VideoPlayer } from 'shared/components/VideoPlayer'

import styles from './TutorialModal.module.scss'
import { TutorialChapter, TutorialModalProps } from './TutorialModal.types'

const formatTimestamp = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

// Chapters are in ascending timestamp order (see useTutorialButton) — the
// active one is the last chapter reached, not the closest one.
const getActiveChapterId = (
  chapters: TutorialChapter[],
  currentTime: number
) => {
  let activeId: string | undefined
  for (const chapter of chapters) {
    if (chapter.timestamp > currentTime) break
    activeId = chapter.id
  }
  return activeId
}

export const TutorialModal: FC<TutorialModalProps> = ({
  isOpen,
  onClose,
  configs
}) => {
  const { t } = useTranslation('translation')
  const videoRef = useRef<HTMLVideoElement>(null)
  const [selectedTutorialId, setSelectedTutorialId] = useState(configs[0]?.id)
  const selectedConfig =
    configs.find(config => config.id === selectedTutorialId) ?? configs[0]
  const [activeChapterId, setActiveChapterId] = useState<string | undefined>(
    selectedConfig?.chapters[0]?.id
  )

  useEffect(() => {
    setSelectedTutorialId(configs[0]?.id)
  }, [configs])

  useEffect(() => {
    setActiveChapterId(selectedConfig?.chapters[0]?.id)
  }, [selectedConfig?.id, selectedConfig?.chapters])

  if (!selectedConfig) return null

  const handleChapterClick = (timestamp: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp
      videoRef.current.play()
    }
  }

  const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
    const nextActiveChapterId = getActiveChapterId(
      selectedConfig.chapters,
      event.currentTarget.currentTime
    )
    setActiveChapterId(prev =>
      prev === nextActiveChapterId ? prev : nextActiveChapterId
    )
  }

  const tutorialMenu = configs.length > 1 && (
    <nav
      className={styles.tutorialMenu}
      aria-label={t('tutorial_modal.tutorials_label')}
    >
      <p className={styles.tutorialsLabel}>
        {t('tutorial_modal.tutorials_label')}
      </p>
      <div className={styles.tutorialList}>
        {configs.map(config => {
          const isSelected = config.id === selectedConfig.id

          return (
            <button
              key={config.id}
              type='button'
              className={cn(styles.tutorialItem, {
                [styles.tutorialItemActive]: isSelected
              })}
              aria-current={isSelected ? 'true' : undefined}
              onClick={() => setSelectedTutorialId(config.id)}
            >
              {config.title}
            </button>
          )
        })}
      </div>
    </nav>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={'88rem'}
      zIndex={1100}
      fitContent
      leftSideBar={tutorialMenu || undefined}
      withHeaderLine
      showCloseButton
      closeOnClickOutside
      layers={[
        {
          title: selectedConfig.title,
          bodyPaddings: true,
          body: (
            <div className={styles.tutorialModal}>
              <div className={styles.videoSection}>
                <VideoPlayer
                  key={selectedConfig.id}
                  ref={videoRef}
                  autoPlay
                  preload='metadata'
                  className={styles.video}
                  src={selectedConfig.videoUrl}
                  labels={{ video: selectedConfig.title }}
                  onTimeUpdate={handleTimeUpdate}
                />
              </div>
              {selectedConfig.chapters.length > 0 && (
                <div className={styles.chaptersSection}>
                  <p className={styles.chaptersLabel}>
                    {t('tutorial_modal.chapters_label')}
                  </p>
                  {selectedConfig.chapters.map(chapter => (
                    <button
                      key={chapter.id}
                      type='button'
                      className={cn(styles.chapterItem, {
                        [styles.chapterItemActive]:
                          activeChapterId === chapter.id
                      })}
                      onClick={() => handleChapterClick(chapter.timestamp)}
                      aria-current={
                        activeChapterId === chapter.id ? 'true' : undefined
                      }
                    >
                      <span className={styles.chapterTitle}>
                        {chapter.title}
                      </span>
                      <span className={styles.chapterTimestamp}>
                        {formatTimestamp(chapter.timestamp)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        }
      ]}
    />
  )
}
